import type { FC } from 'react';
import { memo, useMemo } from 'react';

import { Avatar, useTheme } from '@mui/material';

import { NotificationTagIconContainer } from '@compositeComponents/sidebars/RightSidebar/components/RightSidebarNotifications/RightSidebarNotifications.style';
import {
  CompanySmallIcon,
  ContactsIcon,
  CrownGoldSmallIcon,
  ImageAddIcon,
  NetworkIcon,
} from '@setup/svg-icons/svg-icons';
import { AvatarSizeEnum } from '@shared/enums/avatarSize.enum';
import { AvatarBudgeSize } from '@shared/enums/avatarSize.enum';
import useBgColor from '@shared/hooks/useBgColor';
import { MuiBadge } from '@shared/ui-kit/badges/MuiBadge/MuiBadge';

import { AddIconContainer, CustomBadge, HubsAvatarBadge, OnlineAvatarBadge } from './MuiAvatar.style';
import { avatarSizeStyles, commonAvatarStyles } from './const';

export type MuiAvatarProps = {
  size?: AvatarSizeEnum;
  badgeSize?: AvatarBudgeSize;
  variant?: 'circular' | 'rounded';
  firstName?: string;
  lastName?: string;
  src?: string;
  isOnline?: boolean;
  hubIcon?: string;
  isOwner?: boolean;
  customStyle?: object;
  id: number;
  isAddPhotoIcon?: boolean;
  borderColor?: string;
  borderWidth?: string;
  isConnected?: boolean;
  isCompany?: boolean;
  isDistinguish?: boolean;
  isCursorOnHover?: true;
};

export const MuiAvatar: FC<MuiAvatarProps> = memo(
  ({
    size = AvatarSizeEnum.size28,
    badgeSize,
    variant = 'circular',
    firstName,
    lastName,
    src,
    isOnline = false,
    hubIcon,
    isOwner = false,
    customStyle = {},
    id,
    isAddPhotoIcon = false,
    borderColor,
    borderWidth = '1px',
    isConnected,
    isDistinguish,
    isCompany,
    isCursorOnHover,
  }) => {
    const theme = useTheme();
    const customBorderColor =
      borderColor ?? isOwner ? theme.palette.case.neutral.n0 : theme.palette.case.neutral.n100;

    const avatarStyle = useMemo(() => {
      return {
        ...commonAvatarStyles,
        ...(avatarSizeStyles[size] || {}),
      };
    }, [size]);

    const handleSetName = useMemo(() => {
      if (size === AvatarSizeEnum.size28) {
        return firstName?.slice(0, 1);
      }
      return `${firstName?.slice(0, 1)}${lastName ? lastName?.slice(0, 1) : ''}`;
    }, [firstName, lastName, size]);

    const { bgColor } = useBgColor();

    const getAvatarFill = useMemo(() => {
      return { backgroundColor: !!src ? '' : `${bgColor(id)}` };
    }, [id, bgColor, src]);

    const getBadgeDimensions = (() => {
      if (badgeSize) {
        return badgeSize;
      }
      if (size >= AvatarSizeEnum.size64) {
        return AvatarBudgeSize.size16;
      }
      if (size === AvatarSizeEnum.size24) {
        return AvatarBudgeSize.size10;
      }

      return AvatarBudgeSize.size12;
    })();

    return (
      <MuiBadge
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        badgeContent={<CrownGoldSmallIcon />}
        invisible={!isOwner}
        disableBackground
      >
        <OnlineAvatarBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          size={size}
          invisible={!isOnline}
          sx={{ ...customStyle }}
        >
          <HubsAvatarBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              hubIcon ? (
                <NotificationTagIconContainer disableBorder>
                  <img src={hubIcon} alt="icon" />
                </NotificationTagIconContainer>
              ) : (
                <></>
              )
            }
            size={size}
            invisible={!hubIcon}
          >
            <Avatar
              sx={{
                ...avatarStyle,
                ...getAvatarFill,
                border: `${borderWidth} solid ${customBorderColor} !important`,
                ...(isCursorOnHover && {
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }),
              }}
              variant={variant}
              src={!!src ? `${src}` : ''}
            >
              {handleSetName}
              {isAddPhotoIcon && (
                <AddIconContainer
                  className="add-icon"
                  sx={{
                    display: 'none',
                    ...avatarStyle,
                  }}
                >
                  <ImageAddIcon style={{ color: 'white', width: '20px', height: '20px' }} />
                </AddIconContainer>
              )}
            </Avatar>
            {isConnected && (
              <CustomBadge
                avatarVariant={variant}
                badgeSize={getBadgeDimensions}
                backgroundColor={theme.palette.case.primary.p50}
                iconColor={theme.palette.case.primary.p600}
              >
                <NetworkIcon />
              </CustomBadge>
            )}
            {isDistinguish && (
              <CustomBadge
                avatarVariant={variant}
                badgeSize={getBadgeDimensions}
                backgroundColor={theme.palette.case.neutral.n75}
              >
                <ContactsIcon />
              </CustomBadge>
            )}
            {isCompany && (
              <CustomBadge
                avatarVariant={variant}
                badgeSize={getBadgeDimensions}
                backgroundColor={theme.palette.case.blue.b50}
              >
                <CompanySmallIcon />
              </CustomBadge>
            )}
          </HubsAvatarBadge>
        </OnlineAvatarBadge>
      </MuiBadge>
    );
  },
);
